import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl, formatApiErrorDetail } from "../../lib/api";
import { ArrowLeft, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "../../components/ui/checkbox";

export default function CreateJob() {
  const navigate = useNavigate();
  const [designs, setDesigns] = useState([]);
  const [managers, setManagers] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [addingType, setAddingType] = useState(false);
  const [newType, setNewType] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    product_name: "", product_type: "", photo: "", quantity: "", start_date: new Date().toISOString().slice(0, 10),
    deadline: "", instruction: "", manager_id: "", design_id: "", sample_provided: false, sample_note: "", with_earrings: false,
  });

  useEffect(() => {
    api.get("/designs").then((r) => setDesigns(r.data)).catch(() => {});
    api.get("/managers").then((r) => { setManagers(r.data); if (r.data[0]) setForm((f) => ({ ...f, manager_id: r.data[0].id })); }).catch(() => {});
    api.get("/product-types").then((r) => setProductTypes(r.data)).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addProductType = async () => {
    const name = newType.trim();
    if (!name) return;
    try {
      const { data } = await api.post("/product-types", { name });
      setProductTypes((t) => [...t, data].sort((a, b) => a.name.localeCompare(b.name)));
      set("product_type", data.name);
      setNewType("");
      setAddingType(false);
      toast.success("Product type added");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const pickDesign = (d) => {
    set("design_id", d.id);
    setForm((f) => ({ ...f, design_id: d.id, product_name: d.design_name, photo: d.photo || "", instruction: d.instruction || "" }));
  };

  const onUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("photo", data.path);
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!form.product_name || !form.product_type || !form.photo || !form.quantity || !form.start_date || !form.deadline) {
      toast.error("Please fill product, product type, photo, quantity, start date and deadline.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/jobs", { ...form, quantity: parseInt(form.quantity, 10) });
      toast.success("Job created and sent to manager!");
      navigate("/owner/jobs");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const input = "w-full h-12 px-4 rounded-xl border border-slate-300 focus:border-[#C86D51] focus:ring-2 focus:ring-[#C86D51]/20 outline-none text-base bg-white";

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-slate-200 px-4 py-3 flex items-center gap-3 max-w-lg lg:max-w-3xl mx-auto">
        <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-xl hover:bg-slate-100"><ArrowLeft className="h-5 w-5" /></button>
        <h1 className="font-display text-lg font-bold">Create New Job</h1>
      </header>

      <div className="px-4 py-4 space-y-5 max-w-lg lg:max-w-3xl mx-auto">
        {/* Design quick pick */}
        {designs.length > 0 && (
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-2 block">Pick a saved design (optional)</label>
            <div className="flex gap-3 overflow-x-auto no-scrollbar pb-1">
              {designs.map((d) => (
                <button key={d.id} onClick={() => pickDesign(d)}
                  className={`flex-shrink-0 w-24 rounded-xl overflow-hidden border-2 transition-colors ${form.design_id === d.id ? "border-[#C86D51]" : "border-transparent"}`}>
                  <img src={fileUrl(d.photo)} alt={d.design_name} className="h-20 w-24 object-cover" />
                  <p className="text-[10px] font-semibold p-1 bg-white truncate">{d.design_name}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        <div>
          <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Product / Design Name *</label>
          <input data-testid="create-job-name" className={input} value={form.product_name} onChange={(e) => set("product_name", e.target.value)} placeholder="Copper Bracelet B-104" />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-sm font-semibold text-slate-700">Product Type *</label>
            <button data-testid="add-product-type-toggle" type="button" onClick={() => setAddingType(!addingType)}
              className="text-xs font-bold text-[#9A462B]">+ Add Option</button>
          </div>
          <select data-testid="create-job-product-type" className={input} value={form.product_type} onChange={(e) => set("product_type", e.target.value)}>
            <option value="">Select product type…</option>
            {productTypes.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
          </select>
          {addingType && (
            <div className="flex gap-2 mt-2">
              <input data-testid="new-product-type-input" className={input} value={newType} onChange={(e) => setNewType(e.target.value)} placeholder="New product type name" />
              <button data-testid="save-product-type-button" type="button" onClick={addProductType}
                className="px-5 rounded-xl copper-gradient text-white font-bold text-sm flex-shrink-0">Save</button>
            </div>
          )}
        </div>

        <div>
          <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Product Photo *</label>
          {form.photo ? (
            <div className="relative">
              <img src={fileUrl(form.photo)} alt="preview" className="w-full h-48 object-cover rounded-xl" />
              <button onClick={() => set("photo", "")} className="absolute top-2 right-2 bg-black/60 text-white text-xs px-3 py-1 rounded-full">Change</button>
            </div>
          ) : (
            <label className="flex flex-col items-center justify-center h-40 rounded-xl border-2 border-dashed border-slate-300 bg-white cursor-pointer">
              {uploading ? <Loader2 className="h-6 w-6 animate-spin text-[#C86D51]" /> : <><Upload className="h-7 w-7 text-slate-400 mb-2" /><span className="text-sm text-slate-500 font-medium">Tap to upload (JPG, PNG, WEBP)</span></>}
              <input data-testid="create-job-photo" type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onUpload} />
            </label>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Quantity *</label>
            <input data-testid="create-job-quantity" type="number" min="1" className={input} value={form.quantity} onChange={(e) => set("quantity", e.target.value)} placeholder="500" />
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Manager *</label>
            <select data-testid="create-job-manager" className={input} value={form.manager_id} onChange={(e) => set("manager_id", e.target.value)}>
              {managers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Start Date *</label>
            <input data-testid="create-job-start" type="date" className={input} value={form.start_date} onChange={(e) => set("start_date", e.target.value)} />
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Deadline *</label>
            <input data-testid="create-job-deadline" type="date" className={input} value={form.deadline} onChange={(e) => set("deadline", e.target.value)} />
          </div>
        </div>

        <div>
          <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Instruction (optional)</label>
          <textarea data-testid="create-job-instruction" className="w-full p-4 rounded-xl border border-slate-300 focus:border-[#C86D51] outline-none text-base bg-white" rows="3" value={form.instruction} onChange={(e) => set("instruction", e.target.value)} placeholder="Match approved sample exactly." />
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <Checkbox data-testid="with-earrings-checkbox" checked={form.with_earrings} onCheckedChange={(v) => set("with_earrings", !!v)} className="h-6 w-6" />
            <span className="text-base font-bold text-slate-800">With Earrings</span>
          </label>
          <p className="text-xs text-slate-400 mt-1 ml-9">Mark if this job includes earrings manufacturing.</p>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
          <label className="flex items-center gap-3 cursor-pointer">
            <Checkbox data-testid="sample-provided-checkbox" checked={form.sample_provided} onCheckedChange={(v) => set("sample_provided", !!v)} className="h-6 w-6" />
            <span className="text-base font-bold text-slate-800">Sample Provided</span>
          </label>
          {form.sample_provided && (
            <input data-testid="sample-note-input" className={input} value={form.sample_note} onChange={(e) => set("sample_note", e.target.value)} placeholder="e.g. Use this physical sample for exact size and finishing." />
          )}
        </div>

        <button data-testid="create-job-submit-button" onClick={submit} disabled={saving}
          className="w-full py-4 rounded-xl copper-gradient text-white font-bold text-lg shadow-md active:scale-[0.98] transition-transform disabled:opacity-60">
          {saving ? "Sending…" : "Send to Manager"}
        </button>
      </div>
    </div>
  );
}
